import Element from '../../../Prototype/UI/Element';
import Game from '../../../Game';
import { IGameState } from '../../../State/Game/IGameState';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { applyDefaultButtonAnimation } from '../../../System/UI/UIHelper';
import Renderable from '../../../Component/Renderable';
import ButtonComponent from '../../../Component/UI/ButtonComponent';
import AnimationComponent from '../../../Component/Animation';
import { GameActions } from '../../../State/Game/GameActions';
import { WorldDataHelper } from '../../../Utility/WorldDataHelper';
import StateComponent from '../../../Component/State';
import Timer from '../../../System/Timer/Timers/Timer';
import WorldMap from '../../../Prototype/World/WorldMap';
import PIXIRenderer from '../../../System/Render/PIXIRenderer';
import { StartAnimation } from '../../../System/Animation/AnimationHelper';
import { TextAlignment } from '../../../Prototype/UI/TextAlignment';
import { isTransitioning } from 'Utility/isTransitioning';

export default class TravelButton extends Element {
    public background: Element;
    public button: Element;
    public text: Element;
    public previous: boolean;
    public timer: Timer;
    private renderable: Renderable;

    public create(params: any): void {
        this.previous = params.previous || false;

        super.create({
            name: params.name,
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
                ...params.layout,
            },
            children: [
                {
                    name: 'background',
                    layout: {
                        anchors: [0.5, 0.5, 0.5, 0.5],
                        pivot: [0.5, 0.5],
                        offsets: [0, 0, 0, 0],
                        scale: [this.previous ? -1 : 1, 1],
                    },
                    components: [
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'blank',
                            },
                        },
                    ],
                    children: [
                        {
                            name: 'button',
                            layout: {
                                anchors: [0.5, 0.5, 0.5, 0.5],
                                pivot: [0.5, 0.5],
                                offsets: [0, 1, 0, 1],
                            },
                            components: [
                                {
                                    type: 'input',
                                    data: {
                                        enabled: true,
                                        buttonMode: true,
                                        toggleable: true,
                                        tintOnDisable: true,
                                    },
                                },
                                {
                                    type: 'image',
                                    data: {
                                        cacheKey: 'button_arrow',
                                    },
                                },
                            ],
                            children: [
                                {
                                    name: 'text',
                                    layout: {
                                        anchors: [0.485, 0.5, 0.485, 0.5],
                                        pivot: [0.5, 0.5],
                                        offsets: [0, 2, 0, 2],
                                        scale: [this.previous ? -1 : 1, 1],
                                    },
                                    components: [
                                        {
                                            type: 'text',
                                            data: {
                                                textLocalizationKey: this
                                                    .previous
                                                    ? 'PreviousFarm'
                                                    : 'NextFarm',
                                                style: Game.style.getStyle(
                                                    'HUD_Saga_Map.travel_btn'
                                                ),
                                                bounds: [160, 80],
                                                align: TextAlignment.CENTER,
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
            ...params,
        });
        this.addComponent(AnimationComponent);
        this.addComponent(StateComponent, {
            handleStateChanged: this.canShow.bind(this),
        });
        this.background = this.findChildByName('background') as Element;
        this.button = this.findChildByName('button') as Element;
        this.text = this.findChildByName('text') as Element;

        this.timer = new Timer();
        this.timer.onUpdate = this.onUpdate.bind(this);

        this.renderable = this.getComponent<Renderable>(Renderable);
        this.renderable.visible = this.canShow();

        const button: GameObject = this.button;
        button.addComponent(ButtonComponent, {
            toggleable: true,
        });
        button.on(GameObjectEvent.SELECTED, this.onSelect.bind(this));
        applyDefaultButtonAnimation(button);
    }

    private onUpdate(state: IGameState = Game.store.getState()): void {
        this.setVisible(this.canShow(state));
    }

    private setVisible(shouldAppear: boolean): void {
        if (this.destroyed || this.renderable === undefined) return;

        if (shouldAppear && !this.renderable.visible) {
            StartAnimation(this, 'UIPopIn');
        } else if (!shouldAppear && this.renderable.visible) {
            StartAnimation(this, 'UIPopOut');
        }
    }
    private onSelect(): void {
        if(isTransitioning()) {
            return;
        }
        const state: IGameState = Game.store.getState();
        const worldNumber: number = this.previous
            ? state.worldState.worldNumber - 1
            : state.worldState.worldNumber + 1;
        const limitedWorldNumber: number = Math.max(
            Math.min(WorldDataHelper.maxWorld(), worldNumber),
            1
        );
        GameActions.loadWorld({ worldNumber: limitedWorldNumber });
        this.getComponent<Renderable>(Renderable).visible = false;
    }

    private canShow(state: IGameState = Game.store.getState()): boolean {
        const isPreviousOnFirst: boolean =
            this.previous && state.worldState.worldNumber === 1;
        const isNextOnLast: boolean =
            !this.previous &&
            state.worldState.worldNumber === state.playerState.maxWorld;
        let isAtEnd: boolean = false;
        try {
            if (PIXIRenderer.mapViewport != null) {
                const viewportPosition = -PIXIRenderer.mapViewport.position.x;
                isAtEnd = this.previous
                    ? viewportPosition <= PIXIRenderer.mapViewport.width * 0.05
                    : viewportPosition >= PIXIRenderer.mapViewport.width * 0.8;
            }
        } catch (error) {
            console.warn(error);
        }
        return !isPreviousOnFirst && !isNextOnLast && isAtEnd;
    }

    public destroy() {
        this.timer.onUpdate = () => {};
        super.destroy();
    }
}
